import { Button } from "@/components/ui/button";
import { FileText, Plus } from "lucide-react";
import { ModernDocumentCard, ModernDocumentCardSkeleton } from "@/components/modern-document-card";
import type { DocumentWithCounts } from "@shared/schema";

interface EnhancedDocumentGridProps {
  documents: DocumentWithCounts[];
  onSelectDocument: (document: DocumentWithCounts) => void;
  onCreateDocument: () => void;
  isLoading: boolean;
  organizationId: string;
  onEditDocument?: (document: DocumentWithCounts) => void;
  onDeleteDocument?: (documentId: string) => void;
}

export function EnhancedDocumentGrid({ 
  documents, 
  onSelectDocument, 
  onCreateDocument, 
  isLoading,
  organizationId,
  onEditDocument,
  onDeleteDocument
}: EnhancedDocumentGridProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="h-5 w-32 bg-muted animate-pulse rounded" />
          <div className="h-8 w-24 bg-muted animate-pulse rounded" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <ModernDocumentCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          Documents
          <span className="font-mono ml-1.5 text-foreground">{documents.length}</span>
        </h2>
        <Button 
          onClick={onCreateDocument} 
          size="sm"
          className="h-8 px-3 text-sm"
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          New
        </Button>
      </div>

      {documents.length === 0 ? (
        <div className="border border-dashed border-border rounded-md py-16 flex flex-col items-center">
          <FileText className="h-8 w-8 text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground mb-4">No documents yet</p>
          <Button onClick={onCreateDocument} variant="outline" size="sm" className="h-8">
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Upload Document
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {documents.map((document) => (
            <ModernDocumentCard
              key={document.id}
              document={document}
              onSelect={onSelectDocument}
              onEdit={onEditDocument}
              onDelete={onDeleteDocument}
            />
          ))}
        </div>
      )}
    </div>
  );
}
